<template>
    <div class="global-search" ref="searchContainer">
        <div class="search-input-wrapper">
            <svg class="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                autocomplete="off"
                placeholder="Rechercher..."
                @input="debouncedSearch"
                @focus="onFocus"
                @keydown.escape="closeSearch"
                @keydown.down.prevent="moveSelection(1)"
                @keydown.up.prevent="moveSelection(-1)"
                @keydown.enter.prevent="confirmSelection" />
            <button v-if="searchQuery" class="search-clear" @click="clearSearch">&times;</button>
        </div>
        <div class="search-dropdown" v-if="showResults && (hasResults || isSearching || noResults)">
            <div v-if="isSearching" class="search-state">
                <span class="search-spinner"></span>
                Recherche...
            </div>
            <div v-else-if="noResults" class="search-state search-empty">Aucun résultat pour « {{ searchQuery }} »</div>
            <div v-else class="search-results-list">
                <div class="search-filter-tabs" v-if="Object.keys(groupedResults).length > 1">
                    <button class="search-tab" :class="{ active: !activeFilter }" @click.stop="((activeFilter = null), resetSelection())">Tout</button>
                    <button class="search-tab" v-for="key in Object.keys(groupedResults)" :key="key" :class="{ active: activeFilter === key }" @click.stop="((activeFilter = key), resetSelection())">
                        {{ getTypeLabel(key) }}
                        <span class="tab-count">{{ groupedResults[key].length }}</span>
                    </button>
                </div>
                <div class="search-group" v-for="(items, type) in filteredResults" :key="type">
                    <div class="search-group-header" v-if="!activeFilter">
                        <span class="search-group-label">
                            {{ getTypeLabel(type as string) }}
                            <span class="search-group-count">{{ items.length }}</span>
                        </span>
                        <button
                            class="search-group-more"
                            :class="{ active: isVoirToutActive(type as string) }"
                            :ref="(el) => setItemRef(type as string, VOIR_TOUT_ROW, el as HTMLElement)"
                            v-if="items.length > 5"
                            @click.stop="((activeFilter = type as string), (selectedCol = 0), (selectedRow = -1))">
                            Voir tout
                        </button>
                    </div>
                    <div
                        v-for="(result, i) in activeFilter ? items : items.slice(0, 5)"
                        :key="result.type + '-' + result.id"
                        :ref="(el) => setItemRef(type as string, i, el as HTMLElement)"
                        class="search-result-item"
                        :class="{ active: isActive(type as string, i) }"
                        @click="navigateToResult(result)"
                        @mouseenter="setSelection(type as string, i)">
                        <span class="result-label">{{ cleanLabel(result.label) }}</span>
                        <span class="result-meta" v-if="result.agencyName && props.agencyId == null">{{ result.agencyName }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { useSearchStore } from '../store'
import type { SearchResult, SearchResultType } from '../types'

const props = defineProps<{
    routeMap: Record<string, (id: string, result?: SearchResult) => RouteLocationRaw>
    typeLabels?: Record<string, string>
    agencyId?: string | number | null
}>()

const router = useRouter()
const searchStore = useSearchStore()

const searchQuery = ref('')
const searchResults = ref<SearchResult[]>([])
const showResults = ref(false)
const isSearching = ref(false)
const hasSearched = ref(false)
const searchContainer = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const searchTypes = computed(() => Object.keys(props.routeMap) as SearchResultType[])

const DEFAULT_LABELS: Record<string, string> = {
    Manager: 'Managers',
    User: 'Managers',
    Employee: 'Employés',
    Client: 'Clients',
    Card: 'Cartes',
    Transaction: 'Transactions',
    Agency: 'Établissements'
}

const typeLabels = computed(() => ({ ...DEFAULT_LABELS, ...(props.typeLabels || {}) }))

const getTypeLabel = (type: string) => typeLabels.value[type] || type

const normalizeType = (type: string) => (type === 'User' ? 'Manager' : type)

const cleanLabel = (label: string) => label.replace(/\s*\(\s*\)/g, '').trim()

const groupedResults = computed(() => {
    const groups: Record<string, SearchResult[]> = {}
    for (const result of searchResults.value) {
        const key = normalizeType(result.type)
        if (!groups[key]) groups[key] = []
        groups[key].push(result)
    }
    return groups
})

const selectedCol = ref(-1)
const selectedRow = ref(-1)

const activeFilter = ref<string | null>(null)

const hasResults = computed(() => searchResults.value.length > 0)
const noResults = computed(() => hasSearched.value && !isSearching.value && !hasResults.value && searchQuery.value.trim().length >= 2)

const filteredResults = computed(() => {
    if (!activeFilter.value) return groupedResults.value
    const key = activeFilter.value
    if (groupedResults.value[key]) return { [key]: groupedResults.value[key] }
    return {}
})

const groupKeys = computed(() => Object.keys(filteredResults.value))

const itemRefs: Record<string, HTMLElement> = {}

const setItemRef = (type: string, index: number, el: HTMLElement) => {
    if (el) itemRefs[`${type}-${index}`] = el
}

const scrollToActive = () => {
    const keys = groupKeys.value
    const key = `${keys[selectedCol.value]}-${selectedRow.value}`
    nextTick(() => {
        itemRefs[key]?.scrollIntoView({ block: 'nearest' })
    })
}

const isActive = (type: string, index: number) => {
    return groupKeys.value[selectedCol.value] === type && selectedRow.value === index
}

const setSelection = (type: string, index: number) => {
    selectedCol.value = groupKeys.value.indexOf(type)
    selectedRow.value = index
}

const visibleCount = (key: string) => {
    const total = filteredResults.value[key]?.length || 0
    return activeFilter.value ? total : Math.min(total, 5)
}

const VOIR_TOUT_ROW = -2

const hasVoirTout = (key: string) => {
    return !activeFilter.value && (groupedResults.value[key]?.length || 0) > 5
}

const isVoirToutActive = (type: string) => {
    return groupKeys.value[selectedCol.value] === type && selectedRow.value === VOIR_TOUT_ROW
}

const firstRow = (key: string) => (hasVoirTout(key) ? VOIR_TOUT_ROW : 0)
const lastRow = (key: string) => visibleCount(key) - 1

const nextRow = (key: string, row: number, dir: number) => {
    if (dir === 1) {
        if (row === VOIR_TOUT_ROW) return 0
        if (row < lastRow(key)) return row + 1
        return null
    } else {
        if (row === 0 && hasVoirTout(key)) return VOIR_TOUT_ROW
        if (row > 0) return row - 1
        return null
    }
}

const moveSelection = (dir: number) => {
    if (!hasResults.value) return
    const keys = groupKeys.value
    if (selectedCol.value < 0) {
        selectedCol.value = 0
        selectedRow.value = firstRow(keys[0])
        scrollToActive()
        return
    }
    const key = keys[selectedCol.value]
    const next = nextRow(key, selectedRow.value, dir)
    if (next !== null) {
        selectedRow.value = next
    } else if (dir === 1 && selectedCol.value < keys.length - 1) {
        selectedCol.value++
        selectedRow.value = firstRow(keys[selectedCol.value])
    } else if (dir === -1 && selectedCol.value > 0) {
        selectedCol.value--
        selectedRow.value = lastRow(keys[selectedCol.value])
    }
    scrollToActive()
}

const confirmSelection = () => {
    const keys = groupKeys.value
    if (selectedCol.value < 0) return
    const key = keys[selectedCol.value]
    if (selectedRow.value === VOIR_TOUT_ROW && hasVoirTout(key)) {
        activeFilter.value = key
        selectedCol.value = 0
        selectedRow.value = -1
    } else if (selectedRow.value >= 0) {
        const items = filteredResults.value[key]
        if (items?.[selectedRow.value]) {
            navigateToResult(items[selectedRow.value])
        }
    }
}

const resetSelection = () => {
    selectedCol.value = -1
    selectedRow.value = -1
}

const resetAll = () => {
    resetSelection()
    activeFilter.value = null
}

const debouncedSearch = () => {
    if (searchTimeout) clearTimeout(searchTimeout)
    const query = searchQuery.value.trim()
    if (query.length < 2) {
        searchResults.value = []
        isSearching.value = false
        hasSearched.value = false
        resetAll()
        return
    }
    isSearching.value = true
    showResults.value = true
    searchTimeout = setTimeout(async () => {
        const currentQuery = searchQuery.value.trim()
        if (currentQuery.length < 2) return
        const rawResults: SearchResult[] = await searchStore.search(currentQuery, searchTypes.value, props.agencyId).catch(() => [])
        if (searchQuery.value.trim() === currentQuery) {
            searchResults.value = rawResults
            isSearching.value = false
            hasSearched.value = true
            resetAll()
        }
    }, 300)
}

const onFocus = () => {
    if (searchQuery.value.trim().length >= 2) {
        showResults.value = true
    }
}

const clearSearch = () => {
    searchQuery.value = ''
    searchResults.value = []
    showResults.value = false
    isSearching.value = false
    hasSearched.value = false
    resetAll()
    searchInput.value?.focus()
}

const closeSearch = () => {
    showResults.value = false
    resetAll()
    searchInput.value?.blur()
}

const navigateToResult = (result: SearchResult) => {
    const key = normalizeType(result.type)
    const routeFn = props.routeMap[key]
    if (routeFn) {
        router.push(routeFn(result.id, result))
        searchQuery.value = ''
        searchResults.value = []
        showResults.value = false
    }
}

const onClickOutside = (e: MouseEvent) => {
    if (searchContainer.value && !searchContainer.value.contains(e.target as Node)) {
        showResults.value = false
    }
}

onMounted(() => document.addEventListener('click', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))
</script>

<style lang="scss" scoped>
.global-search {
    position: relative;
    flex: 1;
    max-width: 420px;
    margin: 0 32px;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    border: 1px solid var(--c-color-border-hard);
    border-radius: 8px;
    background: var(--c-color-bg-soft);
    transition:
        border-color 0.2s,
        background 0.2s,
        box-shadow 0.2s;
    padding: 0 10px;
    gap: 8px;

    &:focus-within {
        border-color: var(--c-color-primary);
        background: #fff;
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--c-color-primary) 10%, transparent);
    }

    .search-icon {
        flex-shrink: 0;
        color: var(--c-color-text-soft);
        display: flex;
        align-items: center;
    }

    input {
        flex: 1;
        min-width: 0;
        height: 34px;
        padding: 0;
        margin: 0;
        border: none;
        background: transparent;
        font-size: 0.85rem;
        font-family: 'Sora', inherit;
        line-height: 34px;

        &::placeholder {
            color: var(--c-color-text-soft);
        }

        &:focus {
            outline: none;
        }
    }

    .search-clear {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        cursor: pointer;
        font-size: 1.1rem;
        color: var(--c-color-text-soft);
        padding: 0;
        line-height: 1;
        width: 20px;
        height: 20px;
        border-radius: 4px;

        &:hover {
            color: var(--c-color-text-hard, #333);
            background: rgba(0, 0, 0, 0.05);
        }
    }
}

.search-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: 100%;
    z-index: 100;
    background: #fff;
    border: 1px solid var(--c-color-border-hard);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.search-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 18px 16px;
    font-size: 0.84rem;
    color: var(--c-color-text-soft);
}

.search-empty {
    font-style: italic;
}

.search-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--c-color-border-hard);
    border-top-color: var(--c-color-primary);
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    flex-shrink: 0;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.search-results-list {
    max-height: 400px;
    overflow-y: auto;
    overscroll-behavior: contain;
}

.search-filter-tabs {
    display: flex;
    gap: 4px;
    padding: 8px 8px 4px;
    flex-wrap: wrap;
    border-bottom: 1px solid var(--c-color-border-soft, #eee);
}

.search-tab {
    padding: 4px 10px;
    font-size: 0.75rem;
    font-family: inherit;
    border: 1px solid var(--c-color-border-hard);
    border-radius: 20px;
    background: #fff;
    color: var(--c-color-text-soft);
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;

    .tab-count {
        opacity: 0.5;
        font-size: 0.7rem;
    }

    &:hover {
        border-color: var(--c-color-primary);
        color: var(--c-color-primary);
    }

    &.active {
        background: var(--c-color-primary);
        border-color: var(--c-color-primary);
        color: #fff;

        .tab-count {
            opacity: 0.8;
        }
    }
}

.search-group {
    &:not(:first-child) {
        margin-top: 2px;
    }
}

.search-group-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px 4px;
}

.search-group-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--c-color-text-soft);
}

.search-group-count {
    font-size: 0.65rem;
    font-weight: 600;
    color: var(--c-color-text-soft);
    opacity: 0.6;
}

.search-group-more {
    padding: 2px 8px;
    font-size: 0.72rem;
    font-family: inherit;
    color: var(--c-color-primary);
    background: none;
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;

    &:hover,
    &.active {
        background: color-mix(in srgb, var(--c-color-primary) 8%, transparent);
        border-color: color-mix(in srgb, var(--c-color-primary) 20%, transparent);
    }
}

.search-result-item {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 12px;
    margin: 0 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.08s;

    &:hover {
        background: var(--c-color-bg-soft);
    }

    &.active {
        background: color-mix(in srgb, var(--c-color-primary) 10%, transparent);
    }
}

.result-label {
    font-size: 0.84rem;
    min-width: 0;
}

.result-meta {
    font-size: 0.75rem;
    color: var(--c-color-text-soft);
    white-space: nowrap;
    flex-shrink: 0;
}
</style>
